import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { requestCache } from '../lib/requestCache';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({ projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87', authRequired: false });

export type ThemePreset = 'default' | 'ocean' | 'sunset' | 'forest' | 'cyber' | 'rose' | 'grey' | 'snow' | 'matrix';
export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeConfig {
  preset: ThemePreset;
  mode: ThemeMode;
  primaryHue: number;
  primarySat: number;
  glassIntensity: number;
  glassBlur: number;
  accentColor?: string;
  borderRadius?: number;
}

interface ThemeContextType {
  theme: ThemeConfig;
  updateTheme: (config: Partial<ThemeConfig>) => void;
  setPreset: (preset: ThemePreset) => void;
  setMode: (mode: ThemeMode) => void;
  resetToDefaults: () => void;
  undoChanges: () => void;
  isLoading: boolean;
}

const defaultTheme: ThemeConfig = {
  preset: 'default',
  mode: 'dark',
  primaryHue: 255,
  primarySat: 67,
  glassIntensity: 0.15,
  glassBlur: 20,
};

const themePresets: Record<ThemePreset, Omit<ThemeConfig, 'mode'>> = {
  default: {
    preset: 'default',
    primaryHue: 255,
    primarySat: 67,
    glassIntensity: 0.15,
    glassBlur: 20,
  },
  ocean: {
    preset: 'ocean',
    primaryHue: 200,
    primarySat: 80,
    glassIntensity: 0.18,
    glassBlur: 24,
  },
  sunset: {
    preset: 'sunset',
    primaryHue: 25,
    primarySat: 85,
    glassIntensity: 0.12,
    glassBlur: 16,
  },
  forest: {
    preset: 'forest',
    primaryHue: 140,
    primarySat: 60,
    glassIntensity: 0.2,
    glassBlur: 18,
  },
  cyber: {
    preset: 'cyber',
    primaryHue: 180,
    primarySat: 100,
    glassIntensity: 0.25,
    glassBlur: 28,
  },
  rose: {
    preset: 'rose',
    primaryHue: 330,
    primarySat: 70,
    glassIntensity: 0.14,
    glassBlur: 22,
  },
  grey: {
    preset: 'grey',
    primaryHue: 220,
    primarySat: 10,
    glassIntensity: 0.08,
    glassBlur: 12,
  },
  snow: {
    preset: 'snow',
    primaryHue: 210,
    primarySat: 30,
    glassIntensity: 0.05,
    glassBlur: 16,
  },
  matrix: {
    preset: 'matrix',
    primaryHue: 120,
    primarySat: 100,
    glassIntensity: 0.2,
    glassBlur: 24,
  },
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig>(defaultTheme);
  const [previousTheme, setPreviousTheme] = useState<ThemeConfig>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [saveTimeoutId, setSaveTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Load theme from database on mount
  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUserId(state.user.id);
        await loadThemeFromDB(state.user.id);
      } else {
        // Load from localStorage if not authenticated
        const saved = localStorage.getItem('lemon-ai-theme');
        if (saved) {
          try {
            setTheme(safeJsonParse(saved));
          } catch (err) {
            console.error('Failed to parse saved theme:', err);
          }
        }
        setIsLoading(false);
      }
    });
    
    return unsubscribe;
  }, []);

  const loadThemeFromDB = async (uid: string) => {
    const cacheKey = `theme:${uid}`;
    
    try {
      const settings = await requestCache.fetchWithCache(
        cacheKey,
        async () => {
          return await requestCache.retryWithBackoff(
            async () => {
              return await blink.db.userSettings.list({
                where: { userId: uid },
                limit: 1,
              });
            },
            {
              maxRetries: 1, // Reduced from 2 - theme is not critical
              initialDelay: 3000, // Increased from 2000ms
              maxDelay: 10000, // Reduced from 30000ms - fail faster for non-critical
            }
          );
        },
        {
          ttl: 600000, // Cache for 10 minutes (increased from 5min - theme changes are very infrequent)
        }
      );
      
      if (settings && settings.length > 0) {
        const themeData = settings[0].theme;
        if (themeData) {
          try {
            const parsed = safeJsonParse(themeData);
            setTheme(parsed);
          } catch {
            // If not JSON, treat as preset name
            if (themeData in themePresets) {
              setTheme(themePresets[themeData as ThemePreset]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load theme from DB:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveThemeToDB = async (themeConfig: ThemeConfig) => {
    if (!userId) return;
    
    const cacheKey = `theme:${userId}`;
    
    try {
      // Fetch existing settings with cache to avoid rate limit on reads
      const settings = await requestCache.fetchWithCache(
        `theme-settings:${userId}`,
        async () => {
          return await requestCache.retryWithBackoff(
            async () => {
              return await blink.db.userSettings.list({
                where: { userId },
                limit: 1,
              });
            },
            {
              maxRetries: 1, // Reduced from 2
              initialDelay: 3000, // Increased from 2000ms
              maxDelay: 10000, // Reduced from 30000ms
            }
          );
        },
        {
          ttl: 300000, // Cache for 5 minutes (increased from 1min)
        }
      );
      
      const themeJson = JSON.stringify(themeConfig);
      
      // Use retry logic for write operations
      await requestCache.retryWithBackoff(
        async () => {
          if (settings && settings.length > 0) {
            await blink.db.userSettings.update(settings[0].id, {
              theme: themeJson,
              updatedAt: new Date().toISOString(),
            });
          } else {
            await blink.db.userSettings.create({
              id: `settings_${userId}_${Date.now()}`,
              userId,
              theme: themeJson,
            });
          }
        },
        {
          maxRetries: 2, // Reduced from 3
          initialDelay: 3000, // Increased from 2000ms
          maxDelay: 15000, // Reduced from 30000ms
        }
      );
      
      // Invalidate both caches after successful save
      requestCache.clear(cacheKey);
      requestCache.clear(`theme-settings:${userId}`);
    } catch (error: any) {
      // If rate limit error, don't log as error - it will retry
      if (error?.status === 429 || error?.code === 'RATE_LIMIT_EXCEEDED') {
        console.warn('Theme save throttled due to rate limit, will retry later');
      } else {
        console.error('Failed to save theme to DB:', error);
      }
    }
  };

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('lemon-ai-theme', JSON.stringify(theme));
      applyTheme(theme);
      
      // Save to DB if authenticated - with debouncing to prevent rate limits
      if (userId) {
        // Clear any existing timeout
        if (saveTimeoutId) {
          clearTimeout(saveTimeoutId);
        }
        
        // Debounce DB saves by 5 seconds to batch rapid theme changes (increased from 2s)
        const timeoutId = setTimeout(() => {
          saveThemeToDB(theme);
        }, 5000);
        
        setSaveTimeoutId(timeoutId);
      }
    }
  }, [theme, isLoading, userId]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }
    };
  }, [saveTimeoutId]);

  const applyTheme = (config: ThemeConfig) => {
    const root = document.documentElement;
    
    // Add fade transition class
    root.classList.add('theme-transitioning');
    
    // Map preset names to theme data-attribute values
    const themeMap: Record<ThemePreset, string> = {
      'default': 'naible-gold',
      'ocean': 'midnight-teal',
      'sunset': 'copper-sun',
      'forest': 'forest-mist',
      'cyber': 'cobalt-flux',
      'rose': 'rose-moon',
      'grey': 'silver-stone',
      'snow': 'white-snow',
      'matrix': 'matrix-green',
    };
    
    // Determine actual mode (system preference if mode is 'system')
    let actualMode = config.mode;
    if (config.mode === 'system') {
      actualMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    
    // Apply theme via data attribute (activates themes.css)
    const themeName = themeMap[config.preset] || 'naible-gold';
    root.setAttribute('data-theme', themeName);
    root.setAttribute('data-mode', actualMode);
    
    // Update color-scheme for native browser UI
    root.style.colorScheme = actualMode;
    
    // Calculate additional dynamic colors if needed
    const { primaryHue, primarySat, glassIntensity, glassBlur, accentColor, borderRadius } = config;
    
    // Glass effect variables (custom, not in themes.css)
    root.style.setProperty('--glass-intensity', glassIntensity.toString());
    root.style.setProperty('--glass-blur', `${glassBlur}px`);
    
    // Custom accent color override if provided (must override the theme's accent)
    if (accentColor) {
      // Convert hex to HSL for Tailwind compatibility
      const hex = accentColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;
      
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }
      
      const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      root.style.setProperty('--accent', hsl);
    } else {
      root.style.removeProperty('--accent');
    }
    
    // Custom border radius if provided
    if (borderRadius !== undefined) {
      root.style.setProperty('--radius', `${borderRadius}rem`);
    }
    
    // Force re-render by toggling a dummy class
    root.classList.add('theme-update');
    requestAnimationFrame(() => {
      root.classList.remove('theme-update');
    });
    
    // Remove transition class after animation completes
    setTimeout(() => {
      root.classList.remove('theme-transitioning');
    }, 300);
  };

  const updateTheme = (config: Partial<ThemeConfig>) => {
    setPreviousTheme(theme); // Save current state for undo
    setTheme(prev => ({ ...prev, ...config }));
  };

  const setPreset = (preset: ThemePreset) => {
    setPreviousTheme(theme); // Save current state for undo
    const newTheme = { ...themePresets[preset], mode: theme.mode };
    setTheme(newTheme);
    // Immediately apply theme to ensure visual update
    applyTheme(newTheme);
  };

  const setMode = (mode: ThemeMode) => {
    setPreviousTheme(theme); // Save current state for undo
    const newTheme = { ...theme, mode };
    setTheme(newTheme);
    // Immediately apply theme to ensure visual update
    applyTheme(newTheme);
  };

  const resetToDefaults = () => {
    setPreviousTheme(theme); // Save current state for undo
    setTheme(defaultTheme);
    applyTheme(defaultTheme);
  };

  const undoChanges = () => {
    setTheme(previousTheme);
    applyTheme(previousTheme);
  };

  // Listen to system theme changes when mode is 'system'
  useEffect(() => {
    if (theme.mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Re-apply theme to update data-mode
      applyTheme(theme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, setPreset, setMode, resetToDefaults, undoChanges, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export { themePresets, ThemeProvider };
